import { useCallback, useEffect, useRef, useState } from 'react';
import { compressVideoLocal } from '../utils/compressLocal';

const ACCEPT = 'video/mp4,video/webm,video/quicktime,video/x-m4v,.mp4,.webm,.mov,.m4v,.mkv,.avi';

function formatBytes(n) {
  const bytes = Number(n) || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function percentOf(part, whole) {
  if (!whole) return 0;
  return Math.round((Number(part) / Number(whole)) * 1000) / 10;
}

export default function CompressVideo() {
  const inputRef = useRef(null);
  const previewUrlRef = useRef('');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ ratio: 0, label: '' });
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const clearPreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
    setPreviewUrl('');
  }, []);

  useEffect(() => () => clearPreview(), [clearPreview]);

  function pickFile(next) {
    setError('');
    setResult(null);
    setProgress({ ratio: 0, label: '' });
    clearPreview();
    if (!next) {
      setFile(null);
      return;
    }
    if (!String(next.type || '').startsWith('video/') && !/\.(mp4|webm|mov|m4v|mkv|avi)$/i.test(next.name || '')) {
      setError('Please choose a video file (MP4, WebM, or MOV).');
      setFile(null);
      return;
    }
    setFile(next);
    const url = URL.createObjectURL(next);
    previewUrlRef.current = url;
    setPreviewUrl(url);
  }

  async function compress() {
    if (!file || busy) return;
    setBusy(true);
    setError('');
    setProgress({ ratio: 0.01, label: 'Starting…' });
    setResult((prev) => {
      if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
      return null;
    });
    try {
      const data = await compressVideoLocal(file, {
        onProgress: (p) => setProgress({ ratio: p.ratio, label: p.label }),
      });
      setResult(data);
      setProgress({ ratio: 1, label: 'Done' });
    } catch (e) {
      const msg = e?.message || 'Compression failed';
      const hint =
        file.size > 500 * 1024 * 1024
          ? ' This file is very large for in-browser encoding — try exporting a shorter clip or lower camera quality first.'
          : '';
      setError(`${msg}${hint}`);
    } finally {
      setBusy(false);
    }
  }

  function downloadResult() {
    if (!result?.blob) return;
    const a = document.createElement('a');
    a.href = result.objectUrl;
    a.download = result.filename || 'video-65pct.mp4';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function resetAll() {
    if (result?.objectUrl) URL.revokeObjectURL(result.objectUrl);
    setResult(null);
    setProgress({ ratio: 0, label: '' });
    pickFile(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  const achievedPct = result ? percentOf(result.compressedSize, result.originalSize) : null;
  const pct = Math.round(Math.min(100, Math.max(0, (progress.ratio || 0) * 100)));

  return (
    <main className="video-compress">
      <p className="eyebrow">Naturals Salon</p>
      <h1 className="brand video-home-title">Compress video</h1>
      <p className="lede video-home-lede">
        Compress on this device to about 65% size — no upload to the server. Keep the same resolution
        and frame rate, then download the result locally.
      </p>

      {error ? <div className="error-banner">{error}</div> : null}

      <label
        className={`video-compress-drop${dragOver ? ' is-drag' : ''}${file ? ' has-file' : ''}`}
        onDragEnter={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const next = e.dataTransfer?.files?.[0];
          if (next) pickFile(next);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="video-compress-input"
          disabled={busy}
          onChange={(e) => pickFile(e.target.files?.[0] || null)}
        />
        {!file ? (
          <span className="video-compress-drop-copy">
            <strong>Drop a video here</strong>
            <span>or tap to browse · MP4 / WebM / MOV</span>
          </span>
        ) : (
          <span className="video-compress-drop-copy">
            <strong>{file.name}</strong>
            <span>{formatBytes(file.size)} · ready to compress on this device</span>
          </span>
        )}
      </label>

      {previewUrl ? (
        <div className="video-compress-preview">
          <video src={previewUrl} controls playsInline preload="metadata" />
        </div>
      ) : null}

      <div className="video-home-cta-stack">
        <button
          type="button"
          className="btn btn-primary video-home-cta"
          disabled={!file || busy}
          onClick={compress}
        >
          <span>{busy ? 'Compressing…' : 'Compress to 65%'}</span>
          {!busy ? (
            <span className="cta-chip" aria-hidden="true">
              Local
            </span>
          ) : null}
        </button>
        {file || result ? (
          <button type="button" className="service-select-link video-home-refresh" onClick={resetAll} disabled={busy}>
            Clear
          </button>
        ) : null}
      </div>

      {busy ? (
        <div className="upload-hero video-compress-busy" aria-live="polite">
          <div className="upload-ring spin" />
          <p className="upload-label">{progress.label || 'Working…'}</p>
          <div className="video-compress-progress" aria-hidden="true">
            <div className="video-compress-progress-bar" style={{ width: `${pct}%` }} />
          </div>
          <p className="video-compress-progress-pct">{pct}%</p>
        </div>
      ) : null}

      {result ? (
        <section className="video-compress-result" aria-live="polite">
          <p className="service-select-section-label">Result</p>
          <div className="video-compress-stats">
            <div>
              <span className="video-compress-stat-label">Original</span>
              <strong>{formatBytes(result.originalSize)}</strong>
            </div>
            <div>
              <span className="video-compress-stat-label">Compressed</span>
              <strong>{formatBytes(result.compressedSize)}</strong>
            </div>
            <div>
              <span className="video-compress-stat-label">Size</span>
              <strong>{achievedPct}% of original</strong>
            </div>
          </div>
          {result.note === 'already_efficient' ? (
            <p className="lede">This file was already efficient — output matches the original size.</p>
          ) : (
            <p className="lede">
              Compressed on this device (no server upload). Target 65%; achieved {achievedPct}%.
            </p>
          )}
          {result.objectUrl ? (
            <div className="video-compress-preview">
              <video src={result.objectUrl} controls playsInline preload="metadata" />
            </div>
          ) : null}
          <button type="button" className="btn btn-primary video-home-cta" onClick={downloadResult}>
            <span>Download compressed MP4</span>
            <span className="cta-chip" aria-hidden="true">
              Save
            </span>
          </button>
        </section>
      ) : null}
    </main>
  );
}
